
import { useTheme } from "next-themes"
import { Toaster as Sonner, toast as sonnerToast } from "sonner"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { ReactElement, JSXElementConstructor, useEffect, useState } from "react"

type ToasterProps = React.ComponentProps<typeof Sonner>

// Novos tipos para os toasts
export type ToastType = "success" | "error" | "warning" | "info"

// Extensão do toast padrão para incluir o tipo e avatar
export interface ExtendedToast {
  type?: ToastType;
  title?: string;
  description?: string;
  duration?: number;
  userAvatar?: string; // Added property for user avatar
}

// Função toast personalizada
export const toast = {
  success: (options: Omit<ExtendedToast, "type">) => {
    return sonnerToast.custom((id) => (
      <ToastContent 
        id={id}
        type="success"
        title={options.title || "Success"}
        description={options.description}
        userAvatar={options.userAvatar}
        duration={options.duration || 4000}
      />
    ), {
      duration: options.duration || 4000,
    })
  },
  error: (options: Omit<ExtendedToast, "type">) => {
    return sonnerToast.custom((id) => (
      <ToastContent 
        id={id}
        type="error" 
        title={options.title || "Error"}
        description={options.description}
        userAvatar={options.userAvatar}
        duration={options.duration || 0}
      />
    ), {
      duration: options.duration || 0, // Erro não fecha automaticamente
    })
  },
  warning: (options: Omit<ExtendedToast, "type">) => {
    return sonnerToast.custom((id) => (
      <ToastContent 
        id={id}
        type="warning" 
        title={options.title || "Warning"}
        description={options.description}
        userAvatar={options.userAvatar}
        duration={options.duration || 0}
      />
    ), {
      duration: options.duration || 0, // Aviso não fecha automaticamente
    })
  },
  info: (options: Omit<ExtendedToast, "type">) => {
    return sonnerToast.custom((id) => (
      <ToastContent 
        id={id}
        type="info" 
        title={options.title || "Informational"}
        description={options.description}
        userAvatar={options.userAvatar}
        duration={options.duration || 0}
      />
    ), {
      duration: options.duration || 0, // Info não fecha automaticamente
    })
  },
  custom: sonnerToast.custom,
  dismiss: sonnerToast.dismiss,
}

// Componente para conteúdo do toast
interface ToastContentProps {
  id: string | number;
  type: ToastType;
  title: string;
  description?: string;
  userAvatar?: string;
  duration?: number;
}

const ToastContent = ({
  id,
  type,
  title,
  description,
  userAvatar,
  duration = 4000,
}: ToastContentProps) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (duration > 0) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev - (100 / (duration / 100));
          return newProgress <= 0 ? 0 : newProgress;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [duration]);

  const getIcon = () => {
    switch (type) {
      case "success":
        return (
          <div className="rounded-full bg-green-600 p-1 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.3332 4L5.99984 11.3333L2.6665 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )
      case "error":
        return (
          <div className="rounded-full bg-red-500 p-1 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.6668 5.33325L5.3335 10.6666M5.3335 5.33325L10.6668 10.6666" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        )
      case "warning":
        return (
          <div className="rounded-full bg-yellow-400 p-1 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 6V8.66667M8 11.3333H8.00667M14.6667 8C14.6667 11.6819 11.6819 14.6667 8 14.6667C4.3181 14.6667 1.33333 11.6819 1.33333 8C1.33333 4.3181 4.3181 1.33333 8 1.33333C11.6819 1.33333 14.6667 4.3181 14.6667 8Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )
      case "info":
        return (
          <div className="rounded-full bg-blue-500 p-1 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 11.3333V8M8 5.33333H8.00667M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.3181 11.6819 1.33333 8 1.33333C4.3181 1.33333 1.33333 4.3181 1.33333 8C1.33333 11.6819 4.3181 14.6667 8 14.6667Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )
      default:
        return null
    }
  }

  const getColors = () => {
    switch (type) {
      case "success":
        return "bg-[#F2FCE2] border-l-4 border-green-600"
      case "error":
        return "bg-[#FEECEB] border-l-4 border-red-500"
      case "warning":
        return "bg-[#FEF7CD] border-l-4 border-yellow-400"
      case "info":
        return "bg-[#D3E4FD] border-l-4 border-blue-500"
      default:
        return "bg-white border-l-4 border-gray-400"
    }
  }

  const getProgressColor = () => {
    switch (type) {
      case "success":
        return "bg-green-600"
      case "error":
        return "bg-red-500"
      case "warning":
        return "bg-yellow-400"
      case "info":
        return "bg-blue-500"
      default:
        return "bg-gray-400"
    }
  }

  return (
    <div className={cn("luxury-toast flex flex-col shadow-lg rounded-sm min-w-[320px] max-w-[420px] overflow-hidden", getColors())}>
      <div className="flex items-start p-4 w-full">
        {/* If user avatar is provided, show it */}
        {userAvatar ? (
          <div className="mr-3 mt-0.5">
            <img src={userAvatar} alt="User" className="w-8 h-8 rounded-full object-cover" />
          </div>
        ) : (
          <div className="mr-3 mt-0.5">{getIcon()}</div>
        )}
        <div className="flex-1">
          <h4 className="font-medium text-gray-900">{title}</h4>
          {description && <p className="text-gray-600 mt-1">{description}</p>}
        </div>
        <button 
          onClick={() => sonnerToast.dismiss(id)} 
          className="ml-4 mt-0.5 text-gray-600 hover:text-black p-2 rounded-full hover:bg-gray-200 transition-colors"
        >
          <X size={24} />
        </button>
      </div>
      
      {/* Barra de progresso regressiva */}
      {duration > 0 && (
        <div className="h-1 bg-gray-200">
          <div 
            className={cn("h-full transition-all duration-100 ease-linear", getProgressColor())}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  )
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster-new" 
      toastOptions={{
        classNames: {
          toast: "!p-0 !bg-transparent !border-0 !shadow-none !overflow-visible",
          closeButton: "hidden", // Escondemos o botão padrão pois usamos o nosso personalizado
        },
        duration: 4000,
      }}
      closeButton={false} // Desativamos o botão de fechar padrão
      {...props}
    />
  )
}

export { Toaster, sonnerToast }
